// src/screens/DetailScreen.js
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { vehiclesAPI, usersAPI } from '../services/api';
import { Heart, MapPin, Phone, Mail, Globe, CheckCircle, Star, Clock } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import AppHeader from '../components/AppHeader';

const DetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [busy, setBusy] = useState(false);

  // Fetch vehicle + favorite state
  useEffect(() => {
    const loadVehicle = async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await vehiclesAPI.getOne(id);
        setVehicle(data);

        // check if it's in favorites if user logged in
        if (user) {
          const favs = await usersAPI.getFavorites();
          const isFav = favs.data.some((v) => v._id === id);
          setIsFavorite(isFav);
        } else {
          setIsFavorite(false);
        }
      } catch (err) {
        console.error('Error loading vehicle', err);
        const msg =
          err.response?.data?.message || err.message || 'Failed to load vehicle.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    loadVehicle();
  }, [id, user]);

  const handleToggleFavorite = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      setBusy(true);
      await vehiclesAPI.toggleFavorite(id);
      setIsFavorite((prev) => !prev);
    } catch (err) {
      console.error('Toggle favorite error', err);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-app-text-secondary-light dark:text-app-text-secondary text-sm customer-friendly">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-customer-teal border-t-transparent rounded-full animate-spin mb-3"></div>
          <p>Getting the details...</p>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-app-text-secondary-light dark:text-app-text-secondary text-sm customer-friendly">
        {error || 'Oops! We couldn\'t find that vehicle.'}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full customer-friendly">
      <AppHeader title={`${vehicle.make} ${vehicle.model}`} subtitle={`${vehicle.year}`} />

      <div className="flex-1 overflow-y-auto p-4 pb-20 space-y-4 text-sm">
        {/* Image */}
        <div className="relative">
          <img
            src={vehicle.image}
            alt={`${vehicle.make} ${vehicle.model}`}
            className="w-full h-60 object-cover rounded-2xl"
          />

          {/* Heart button overlay */}
          <button
            onClick={handleToggleFavorite}
            disabled={busy}
            className={`absolute top-3 right-3 p-2 rounded-full border shadow-md ${
              isFavorite
                ? 'bg-red-500/20 border-red-400'
                : 'bg-app-input-light/80 dark:bg-app-input/80 border-app-input-border-light dark:border-app-input-border'
            }`}
            title={isFavorite ? 'Unfavorite' : 'Favorite'}
          >
            <Heart
              size={20}
              className={isFavorite ? 'fill-red-500 text-red-500' : 'text-app-text-secondary-light dark:text-app-text-secondary'}
            />
          </button>
        </div>

        {/* Info */}
        <div className="space-y-1">
          <div className="text-lg font-semibold">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </div>
          <div className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">
            ${vehicle.price?.toLocaleString()} • {vehicle.type}
          </div>
          {vehicle.mileage && (
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              {vehicle.mileage.toLocaleString()} miles
            </div>
          )}
          {vehicle.transmission && (
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Transmission: {vehicle.transmission}</div>
          )}
          {vehicle.fuel && (
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Fuel type: {vehicle.fuel}</div>
          )}
          {vehicle.vin && (
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">VIN: {vehicle.vin}</div>
          )}
        </div>

        {/* Dealer Info */}
        {vehicle.dealer && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Link
                  to={`/dealer/${vehicle.dealer._id}/info`}
                  className="font-semibold text-base text-customer-teal hover:text-customer-teal-hover transition-colors"
                >
                  {vehicle.dealer.name}
                </Link>
                {vehicle.dealer.verified && (
                  <CheckCircle className="w-4 h-4 text-app-success" title="Verified Dealer" />
                )}
              </div>
              {vehicle.dealer.rating && vehicle.dealer.rating.count > 0 && (
                <div className="flex items-center gap-1 bg-app-warning/10 px-2 py-1 rounded-lg">
                  <Star className="w-3 h-3 fill-app-warning text-app-warning" />
                  <span className="text-xs font-semibold text-app-text-light dark:text-app-text">
                    {vehicle.dealer.rating.average.toFixed(1)}
                  </span>
                  <span className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">
                    ({vehicle.dealer.rating.count})
                  </span>
                </div>
              )}
            </div>

            {/* Address */}
            {vehicle.dealer.address && (
              <div className="flex items-start gap-2 text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {[
                    vehicle.dealer.address.street,
                    vehicle.dealer.address.city,
                    vehicle.dealer.address.state && vehicle.dealer.address.zip
                      ? `${vehicle.dealer.address.state} ${vehicle.dealer.address.zip}`
                      : vehicle.dealer.address.state || vehicle.dealer.address.zip
                  ].filter(Boolean).join(', ')}
                </span>
              </div>
            )}

            {/* Phone */}
            {vehicle.dealer.phone && (
              <div className="flex items-center gap-2 text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                <Phone className="w-4 h-4" />
                <a
                  href={`tel:${vehicle.dealer.phone}`}
                  className="hover:text-customer-teal transition-colors"
                >
                  {vehicle.dealer.phone}
                </a>
              </div>
            )}

            {/* Email */}
            {vehicle.dealer.email && (
              <div className="flex items-center gap-2 text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                <Mail className="w-4 h-4" />
                <a
                  href={`mailto:${vehicle.dealer.email}`}
                  className="hover:text-customer-teal transition-colors"
                >
                  {vehicle.dealer.email}
                </a>
              </div>
            )}

            {/* Website */}
            {vehicle.dealer.website && (
              <div className="flex items-center gap-2 text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                <Globe className="w-4 h-4" />
                <a
                  href={vehicle.dealer.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-customer-teal transition-colors"
                >
                  {vehicle.dealer.website.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}

            {/* Hours - show if dealer is open */}
            {vehicle.dealer.hours && vehicle.dealer.hours.length > 0 && (() => {
              const now = new Date();
              const dayOfWeek = now.getDay();
              const currentTime = now.toTimeString().slice(0, 5);
              const todayHours = vehicle.dealer.hours.find(h => h.day === dayOfWeek);
              const isOpen = todayHours && todayHours.isOpen && currentTime >= todayHours.open && currentTime <= todayHours.close;

              return (
                <div className="flex items-center gap-2 text-xs">
                  <Clock className={`w-4 h-4 ${isOpen ? 'text-app-success' : 'text-app-error'}`} />
                  <span className={`font-medium ${isOpen ? 'text-app-success' : 'text-app-error'}`}>
                    {isOpen ? 'Open Now' : 'Closed'}
                  </span>
                  {todayHours && todayHours.isOpen && (
                    <span className="text-app-text-secondary-light dark:text-app-text-secondary">
                      ({todayHours.open} - {todayHours.close})
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* CTA */}
        <div className="pt-4">
          <button
            onClick={() => navigate(`/vehicle/${id}/appointment`)}
            className="w-full mt-4 py-4 rounded-2xl bg-gradient-to-r from-customer-teal to-customer-teal-hover hover:from-customer-teal-hover hover:to-customer-teal-active text-white font-bold text-base shadow-lg shadow-customer-teal/30 transition-all duration-300 hover:scale-[1.02]">
              Let's Take It for a Spin!
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailScreen;
